import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Loader2, QrCode, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  addAccount,
  formatAccountLabel,
  UNPAIRED_ACCOUNT_LABEL,
  type Account,
} from '@/features/accounts/accounts.api';
import {
  qrErrorStatus,
  QR_REFRESH_INTERVAL_MS,
  useAccountQr,
} from '@/features/accounts/useAccountQr';
import { accountsQueryKey, useAccounts } from '@/features/accounts/useAccounts';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

interface AddAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Two-step "add a WhatsApp account" dialog (M6, admin-only; reworked 2026-08).
 *
 * Step 1 — create:
 *   NO input field. Operators used to type the `external_account_id` here and
 *   a `wa__<digits>` double-underscore typo paired a live phone under a ghost
 *   account (2026-08-05 production incident) — so naming moved server-side:
 *   the Hub auto-generates the id and the account is later named/displayed by
 *   the paired phone's number (`formatAccountLabel`). Step 1 is an explainer
 *   plus a single 「生成配对二维码」 button that POSTs /api/v1/accounts with an
 *   empty body.
 *   - 201 → advance to Step 2 with the new account.
 *   - 503 → inline "网关暂时不可用，请稍后重试" with a retry button.
 *   - anything else (409/422 can no longer happen without a client-sent id,
 *     kept as a fallback) → generic inline error.
 *
 * Step 2 — QR:
 *   Reuses `useAccountQr` (the same hook QrRequiredDialog uses) to poll every
 *   15 s. Watches the LIVE account status from the `useAccounts` cache and
 *   auto-closes with a success toast when the status leaves `qr_required`
 *   (→ `connecting` or `connected`). The toast names the account via
 *   `formatAccountLabel` when the paired phone is already known, and falls
 *   back to generic wording otherwise — the auto-generated epoch id must
 *   never surface. 503/transient errors are shown inline and polling
 *   continues.
 */
export function AddAccountDialog({
  open,
  onOpenChange,
}: AddAccountDialogProps): React.JSX.Element {
  // Track the two-step state: null = Step 1, non-null = Step 2 (QR).
  const [newAccount, setNewAccount] = useState<Account | null>(null);

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      // Reset to Step 1 when the dialog is closed for any reason (X button,
      // Escape, or programmatic close after success). The QR cache cleanup is
      // handled by the Step2QrView's own useEffect (same pattern as
      // QrRequiredDialog).
      setNewAccount(null);
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {newAccount === null ? (
          <Step1CreateView
            onSuccess={(account) => setNewAccount(account)}
            onCancel={() => handleOpenChange(false)}
          />
        ) : (
          <Step2QrView
            account={newAccount}
            onClose={() => handleOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — explainer + create button (no operator-chosen id)
// ---------------------------------------------------------------------------

interface Step1CreateViewProps {
  onSuccess: (account: Account) => void;
  onCancel: () => void;
}

function Step1CreateView({ onSuccess, onCancel }: Step1CreateViewProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [is503, setIs503] = useState(false);

  async function handleCreate(): Promise<void> {
    setServerError(null);
    setIs503(false);
    setIsSubmitting(true);
    try {
      const account = await addAccount();
      onSuccess(account);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 503) {
          setIs503(true);
          setServerError('网关暂时不可用，请稍后重试');
        } else {
          // 409/422 can no longer happen now that the client sends no id;
          // keep a generic fallback for any unexpected server answer.
          setServerError('提交失败，请稍后重试');
        }
      } else {
        setServerError('提交失败，请检查网络连接');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>添加 WhatsApp 账号</DialogTitle>
        <DialogDescription>
          系统将自动生成配对二维码；扫码后账号以配对手机的号码命名（如
          0165552121）。
        </DialogDescription>
      </DialogHeader>

      {/* Re-pairing an OLD number here mints a second row for it; steer
          operators to the existing row's 重扫 / 重试 instead, and say what
          happens if they add anyway (the hub auto-hides the old row once the
          new one connects — `archived_at`, auto-supersede). */}
      <p data-testid="add-account-repair-hint" className="text-xs text-muted-foreground">
        旧号重新连接请在原条目上点重扫/重试，不要新增；若仍新增，旧条目会在新号连上后自动隐藏。
      </p>

      {serverError !== null && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span className="flex-1">{serverError}</span>
          {is503 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void handleCreate()}
              disabled={isSubmitting}
              className="shrink-0"
            >
              <RefreshCw className="mr-1.5 size-3.5" />
              重试
            </Button>
          )}
        </div>
      )}

      <DialogFooter className="gap-2 sm:gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          取消
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleCreate()}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 size-4 animate-spin" />
          ) : (
            <QrCode className="mr-2 size-4" />
          )}
          {isSubmitting ? '生成中…' : '生成配对二维码'}
        </Button>
      </DialogFooter>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — QR display + auto-close
// ---------------------------------------------------------------------------

interface Step2QrViewProps {
  account: Account;
  onClose: () => void;
}

/**
 * QR pairing step.
 *
 * Reuses `useAccountQr` (polls every `QR_REFRESH_INTERVAL_MS` while mounted) to
 * render the rotating pairing QR, and detects a successful pair off the account
 * status — mirroring QrRequiredDialog:
 *   - Success is driven by the LIVE account status from `useAccounts` (the WS
 *     `account.status` projection + the 30s poll keep it current). When it
 *     leaves `qr_required` the account has been paired: toast + close.
 *     `connected` → "已上线", `connecting` → "扫码成功，正在连接", and
 *     `down`/`banned` → close silently (don't falsely claim success).
 *   - A 409 from the QR endpoint ("account is not awaiting QR") is a backstop
 *     close signal for the same transition, in case the accounts cache lags.
 *   - 503 / transient errors → inline "gateway unreachable" message, polling
 *     continues (same as QrRequiredDialog).
 *
 * Naming: the account was just auto-created, so its label is normally
 * {@link UNPAIRED_ACCOUNT_LABEL} until the Hub records the paired phone. All
 * naming goes through `formatAccountLabel` on the LIVE cache row; when the
 * phone isn't known yet the toasts use generic wording — the auto-generated
 * epoch id must never surface.
 *
 * Why watch `useAccounts` and not the QR response's `account_status` echo: the
 * QR endpoint only returns 200 while the account is still `qr_required` (it
 * 409s the instant it leaves), so that echo can NEVER carry a non-`qr_required`
 * value. The `account` prop is likewise a static 201 snapshot (`qr_required`)
 * that never updates, so the live cache row is the only signal that actually
 * moves.
 *
 * Note: we do NOT call onClose in a cleanup effect; that would double-fire if
 * the dialog unmounts for reasons other than our explicit close. We let the
 * Dialog handle cleanup via `onOpenChange`.
 */
function Step2QrView({ account, onClose }: Step2QrViewProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const { data: qr, error, isLoading, isFetching, refetch } = useAccountQr(
    account.id,
    { enabled: true },
  );
  // Live account row from the accounts cache (WS `account.status` projection
  // + 30s poll). The `account` prop is a static 201 snapshot (`qr_required`)
  // that never moves, and the QR response's `account_status` echo is pinned to
  // `qr_required` (the endpoint 409s the moment it leaves) — so this cache row
  // is the only signal that actually transitions when the pair succeeds. It
  // also carries the phone-derived name once the Hub records the pairing.
  const { data: accounts } = useAccounts();
  const liveAccount = accounts?.find((a) => a.id === account.id);
  const liveStatus = liveAccount?.status ?? account.status;
  const label = formatAccountLabel(liveAccount ?? account);
  // True once the account has a phone-derived display name to show; while
  // false the toasts fall back to generic wording (never the epoch id).
  const hasName = label !== UNPAIRED_ACCOUNT_LABEL;

  // Guard against firing the success toast + close more than once if the
  // component re-renders multiple times while the status is non-qr_required.
  const closedRef = useRef(false);

  const errorStatus = qrErrorStatus(error);
  // Show inline error only for non-4xx errors (503, network failure). 404/409
  // are terminal signals handled by the effects below (close, not a banner).
  const showInlineError =
    error !== null && errorStatus !== 404 && errorStatus !== 409;

  // Success / close: the account has left `qr_required` (been paired) as soon
  // as EITHER the live accounts row flips off it (WS/poll — timelier and gives
  // the exact state) OR the QR endpoint 409s ("not awaiting QR"; a backstop for
  // when the accounts cache lags). Toast wording mirrors QrRequiredDialog: a
  // real success for connected/connecting, silent close for down/banned (paired
  // but immediately unhealthy — the sidebar dot shows the real state).
  useEffect(() => {
    if (closedRef.current) return;
    const leftQrRequired = liveStatus !== 'qr_required' || errorStatus === 409;
    if (!leftQrRequired) return;
    closedRef.current = true;
    if (liveStatus === 'connected') {
      toast.success(
        hasName ? `账号已上线 — ${label} 配对成功。` : '账号已上线，配对成功。',
      );
    } else if (liveStatus === 'connecting') {
      toast.success(
        hasName ? `${label}：扫码成功，正在连接…` : '扫码成功，正在连接…',
      );
    } else if (liveStatus === 'qr_required') {
      // 409 backstop fired before the accounts cache caught up: we know it left
      // `qr_required` but not the exact state yet (most often `connecting` at
      // this instant) — a generic success with NO status claim is right.
      toast.success('扫码成功。');
    }
    // down / banned → close silently (mirrors QrRequiredDialog); we don't
    // falsely claim "上线" for a number that paired straight into an error.
    void queryClient.invalidateQueries({ queryKey: accountsQueryKey });
    onClose();
  }, [liveStatus, errorStatus, queryClient, onClose, label, hasName]);

  // 404 — the account row vanished (deleted right after creation). Rare; toast
  // clearly and close. Generic wording: the auto-generated id means nothing to
  // the operator.
  useEffect(() => {
    if (closedRef.current) return;
    if (errorStatus === 404) {
      closedRef.current = true;
      toast.error('账号已不存在，请重新添加。');
      void queryClient.invalidateQueries({ queryKey: accountsQueryKey });
      onClose();
    }
  }, [errorStatus, queryClient, onClose]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>扫描配对 QR</DialogTitle>
        <DialogDescription>
          在手机 WhatsApp 上扫描此二维码（设置 &rarr; 已关联设备 &rarr;
          关联设备）完成配对。
          {hasName ? (
            <>
              {' '}
              账号：
              <span className="font-medium text-foreground">{label}</span>
            </>
          ) : (
            ' 扫码后账号将自动以配对手机的号码命名。'
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col items-center gap-3 py-4">
        {isLoading && (
          <div className="flex h-64 w-64 items-center justify-center rounded-md border border-dashed bg-muted/30">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && showInlineError && (
          <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 px-4 text-center text-sm text-muted-foreground">
            <p>网关暂时不可用，请稍候。</p>
            <p className="text-xs">每 {QR_REFRESH_INTERVAL_MS / 1000} 秒自动重试。</p>
          </div>
        )}

        {!isLoading && !showInlineError && qr && qr.image_base64 && (
          <img
            src={`data:image/png;base64,${qr.image_base64}`}
            alt={`WhatsApp 配对 QR — ${label}`}
            className="size-64 rounded-md border bg-white p-2"
          />
        )}

        {!isLoading && !showInlineError && qr && !qr.image_base64 && (
          <div className="flex h-64 w-64 flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-muted/30 px-4 text-center text-sm text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
            <p>等待网关生成 QR 码...</p>
          </div>
        )}

        {qr?.message && (
          <p className="text-xs text-muted-foreground">{qr.message}</p>
        )}

        <p className="text-xs text-muted-foreground">
          每 {QR_REFRESH_INTERVAL_MS / 1000} 秒刷新。
          {isFetching && qr ? '更新中...' : null}
        </p>
      </div>

      <DialogFooter className="gap-2 sm:gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={isFetching ? 'mr-2 size-4 animate-spin' : 'mr-2 size-4'}
          />
          刷新
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          关闭
        </Button>
      </DialogFooter>
    </>
  );
}
